import React, {useState, useMemo, useEffect} from 'react';
import { CreditCard, Wallet, Users, TrendingUp, Download, Search, ChevronDown, ChevronUp, Filter } from 'lucide-react';
import * as XLSX from 'xlsx';
import DashboardStats from './billing/DashboardStats';
import { saveAs } from 'file-saver';
import api from '../api/client';
import CompanyBillingModal from './billing/CompanyBillingModal';

interface User {
  id: string;
  name: string;
  email: string;
  companyId?: string;
  companyName?: string;
  company: string;
  role?: string;
  paid: boolean;
  paymentStatus?: string;
  tariff?: 'month' | 'quarter' | 'year';
  demoExpiresAt?: string;
  createdAt?: string;
}

const TARIFFS = {
  month: { label: 'Месяц', price: 2000 },
  quarter: { label: 'Квартал (3 мес)', price: 4500 },
  year: { label: 'Год', price: 15000 }
};

export default function BillingPanel({ users }: { users: any[] }) {
  const [activeTab, setActiveTab] = useState<'companies' | 'transactions' >('companies');
  const [histDateFrom, setHistDateFrom] = useState('');
  const [histDateTo, setHistDateTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCompany, setExpandedCompany] = useState<string | null>(null);
  const [selectedCompanyForBilling, setSelectedCompanyForBilling] = useState<any>(null);

  // Логика фильтрации пользователей по поиску
  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;
    const lower = searchTerm.toLowerCase();
    return users.filter(u => 
      (u.name || '').toLowerCase().includes(lower) || 
      (u.email || '').toLowerCase().includes(lower) || 
      (u.companyName || u.company || '').toLowerCase().includes(lower)
    );
  }, [users, searchTerm]);

  // Группировка по компаниям и расчет
  const billingData = useMemo(() => {
    const companiesMap: Record<string, { users: User[], companyName: string }> = {};

    filteredUsers.forEach(u => {
      const compKey = u.companyId || u.companyName || u.company || 'Без компании';
      const compName = u.companyName || u.company || 'Без компании';
      if (!companiesMap[compKey]) {
        companiesMap[compKey] = { users: [], companyName: compName };
      }
      companiesMap[compKey].users.push(u);
    });

    let totalMRR = 0;
    let totalUsersCount = 0;
    let activeCompanies = 0;

    const companiesList = Object.keys(companiesMap).map(compId => {
      const comp = companiesMap[compId];
      const totalUsers = comp.users.length;
      
      const paidUser = comp.users.find(u => u.paid || u.paymentStatus === 'paid');
      const isActive = !!paidUser;
      
      const tariffKey = (paidUser?.tariff as keyof typeof TARIFFS) || 'month';
      const tariffPrice = TARIFFS[tariffKey].price;
      
      const extraUsers = totalUsers > 3 ? totalUsers - 3 : 0;
      const totalCost = isActive ? (1 + extraUsers) * tariffPrice : 0;

      const endDate = paidUser?.demoExpiresAt ? new Date(paidUser.demoExpiresAt) : null;
      const endDateStr = endDate ? endDate.toLocaleDateString('ru-RU') : '—';

      if (isActive) {
        totalMRR += totalCost;
        activeCompanies += 1;
      }
      totalUsersCount += totalUsers;

      return {
        id: compId,
        name: comp.companyName,
        totalUsers,
        extraUsers,
        tariffLabel: TARIFFS[tariffKey].label,
        tariffPrice,
        totalCost,
        isActive,
        endDateStr,
        users: comp.users
      };
    });

    companiesList.sort((a, b) => {
      if (a.isActive && !b.isActive) return -1;
      if (!a.isActive && b.isActive) return 1;
      return b.totalUsers - a.totalUsers;
    });

    // Генерируем список транзакций (на основе оплаченных пользователей)
    const transactions = filteredUsers
      .filter(u => u.paid || u.paymentStatus === 'paid')
      .map(u => {
        const tKey = (u.tariff as keyof typeof TARIFFS) || 'month';
        return {
          id: `TRX-${u.id.slice(-4)}`,
          date: u.createdAt ? new Date(u.createdAt).toLocaleDateString('ru-RU') : '—',
          company: u.companyName || u.company,
          user: u.name || u.email,
          tariff: TARIFFS[tKey].label,
          amount: TARIFFS[tKey].price
        };
      }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    return { companiesList, totalMRR, totalUsersCount, activeCompanies, transactions };
  }, [filteredUsers]);

  // ─── Экспорт в Excel ───
  const exportExcel = (type: 'general' | 'company', compData?: any) => {
    const wb = XLSX.utils.book_new();

    if (type === 'general') {
      // Лист 1: Сводка по компаниям
      const wsCompanies = XLSX.utils.json_to_sheet(billingData.companiesList.map(c => ({
        'Компания': c.name,
        'Пользователей': c.totalUsers,
        'Тариф': c.tariffLabel,
        'Стоимость тарифа': c.tariffPrice,
        'Доп. места (сверх 3)': c.extraUsers,
        'Итого к оплате': c.totalCost,
        'Оплачено до': c.endDateStr,
        'Статус': c.isActive ? 'Оплачен' : 'Не оплачен'
      })));
      XLSX.utils.book_append_sheet(wb, wsCompanies, 'Сводка по компаниям');

      // Лист 2: Транзакции
      if (billingData.transactions.length) {
        const wsTrx = XLSX.utils.json_to_sheet(billingData.transactions);
        XLSX.utils.book_append_sheet(wb, wsTrx, 'История платежей');
      }

      saveAs(new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], { type: 'application/octet-stream' }), `billing_general_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } 
    else if (type === 'company' && compData) {
      // Экспорт конкретной компании (все её пользователи)
      const wsUsers = XLSX.utils.json_to_sheet(compData.users.map((u: User) => ({
        'ID': u.id,
        'Имя': u.name,
        'Email': u.email,
        'Роль': u.role || 'Пользователь',
        'Тариф': TARIFFS[(u.tariff as keyof typeof TARIFFS) || 'month'].label,
        'Статус оплаты': u.paid ? 'Оплачен' : 'Не оплачен',
        'Дата окончания': u.demoExpiresAt ? new Date(u.demoExpiresAt).toLocaleDateString('ru-RU') : '—'
      })));
      XLSX.utils.book_append_sheet(wb, wsUsers, 'Пользователи компании');
      
      const safeName = compData.name.replace(/[^a-zA-Z0-9а-яА-Я]/g, '_');
      saveAs(new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], { type: 'application/octet-stream' }), `company_${safeName}.xlsx`);
    }
  };

  const glassStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.65)',
    backdropFilter: 'blur(20px)',
    borderRadius: 20,
    padding: 24,
    border: '1px solid rgba(255,255,255,0.8)',
    boxShadow: '0 8px 32px rgba(31,38,135,0.07)',
    marginBottom: 20,
  };

  const tabStyle = (isActive: boolean) => ({
    padding: '10px 20px',
    background: isActive ? '#2575fc' : 'transparent',
    color: isActive ? '#fff' : '#64748b',
    border: 'none',
    borderRadius: 12,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s'
  });

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', paddingBottom: 40 }}>
      {/* Заголовок и Кнопка экспорта */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #f59e0b, #ef4444)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
            <CreditCard size={24} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>Биллинг и Финансы</h1>
            <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}>Управление платежами и лицензиями</p>
          </div>
        </div>
        <button onClick={() => exportExcel('general')} style={{ background: '#10b981', color: '#fff', border: 'none', padding: '12px 20px', borderRadius: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Download size={18} /> Выгрузить всё в Excel
        </button>
      </div>

      {/* Фильтры и Поиск */}
      <div style={{ ...glassStyle, padding: 16, display: 'flex', gap: 16, alignItems: 'center' }}>
        <Filter size={20} color="#64748b" />
        <input 
          type="text" 
          placeholder="Поиск по компании, имени или email..." 
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{ flex: 1, padding: '12px 16px', borderRadius: 12, border: '1px solid #cbd5e1', outline: 'none', fontSize: 14 }}
        />
      </div>

      {/* KPI Карточки */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20, marginBottom: 24 }}>
        <div style={glassStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', marginBottom: 8 }}>
            <Wallet size={16} /> <span style={{ fontSize: 14, fontWeight: 600 }}>Общий MRR</span>
          </div>
          <div style={{ fontSize: 28, fontWeight: 800 }}>{billingData.totalMRR.toLocaleString('ru-RU')} ₽</div>
        </div>
        <div style={glassStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', marginBottom: 8 }}>
            <TrendingUp size={16} /> <span style={{ fontSize: 14, fontWeight: 600 }}>Активные компании</span>
          </div>
          <div style={{ fontSize: 28, fontWeight: 800 }}>{billingData.activeCompanies}</div>
        </div>
        <div style={glassStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', marginBottom: 8 }}>
            <Users size={16} /> <span style={{ fontSize: 14, fontWeight: 600 }}>Всего пользователей</span>
          </div>
          <div style={{ fontSize: 28, fontWeight: 800 }}>{billingData.totalUsersCount}</div>
        </div>
      </div>

      {/* Вкладки */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button style={tabStyle(activeTab === 'companies')} onClick={() => setActiveTab('companies')}>По компаниям</button>
        <button style={tabStyle(activeTab === 'transactions')} onClick={() => setActiveTab('transactions')}>История платежей</button>
      </div>

      {/* Контент вкладки "Компании" */}
      {activeTab === 'companies' && (<div>
        <DashboardStats transactions={billingData.transactions} />

        <div style={glassStyle}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#64748b' }}>
                  <th style={{ padding: '12px 8px', fontWeight: 600, width: 40 }}></th>
                  <th style={{ padding: '12px 8px', fontWeight: 600 }}>Компания</th>
                  <th style={{ padding: '12px 8px', fontWeight: 600 }}>Пользователей</th>
                  <th style={{ padding: '12px 8px', fontWeight: 600 }}>Тариф</th>
                  <th style={{ padding: '12px 8px', fontWeight: 600 }}>К оплате</th>
                  <th style={{ padding: '12px 8px', fontWeight: 600 }}>Статус</th>
                  <th style={{ padding: '12px 8px', fontWeight: 600 }}>Excel</th>
                </tr>
              </thead>
              <tbody>
                {billingData.companiesList.map(c => (
                  <React.Fragment key={c.id}>
                    <tr 
                      style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: expandedCompany === c.id ? 'rgba(37,117,252,0.04)' : 'transparent' }}
                      onClick={() => setExpandedCompany(expandedCompany === c.id ? null : c.id)}
                    >
                      <td style={{ padding: '16px 8px', textAlign: 'center', color: '#64748b' }}>
                        {expandedCompany === c.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </td>
                      <td style={{ padding: '16px 8px', fontWeight: 600 }}>{c.name} <button onClick={(e) => { e.stopPropagation(); setSelectedCompanyForBilling(c); }} style={{ marginLeft: 8, padding: '4px 8px', background: '#e0f2fe', color: '#0369a1', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Реквизиты</button></td>
                      <td style={{ padding: '16px 8px' }}>{c.totalUsers} чел.</td>
                      <td style={{ padding: '16px 8px' }}>{c.tariffLabel}</td>
                      <td style={{ padding: '16px 8px', fontWeight: 600 }}>
                        {c.totalCost.toLocaleString('ru-RU')} ₽
                        {c.extraUsers > 0 && <span style={{ fontSize: 11, color: '#ef4444', display: 'block' }}>+{c.extraUsers} мест</span>}
                      </td>
                      <td style={{ padding: '16px 8px' }}>
                        <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, color: c.isActive ? '#10b981' : '#ef4444', background: c.isActive ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)' }}>
                          {c.isActive ? 'Оплачен' : 'Не оплачен'}
                        </span>
                      </td>
                      <td style={{ padding: '16px 8px' }}>
                        <button 
                          onClick={(e) => { e.stopPropagation(); exportExcel('company', c); }}
                          style={{ background: 'none', border: 'none', color: '#2575fc', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                        >
                          <Download size={16} />
                        </button>
                      </td>
                    </tr>
                    {/* Раскрывающийся список пользователей */}
                    {expandedCompany === c.id && (
                      <tr>
                        <td colSpan={7} style={{ padding: 0, background: '#f8fafc' }}>
                          <div style={{ padding: '16px 32px', borderBottom: '1px solid #e2e8f0' }}>
                            <h4 style={{ margin: '0 0 12px 0', fontSize: 14, color: '#64748b' }}>Пользователи компании {c.name}</h4>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                              <thead>
                                <tr style={{ color: '#94a3b8', textAlign: 'left' }}>
                                  <th style={{ padding: '8px 0' }}>Имя</th>
                                  <th style={{ padding: '8px 0' }}>Email</th>
                                  <th style={{ padding: '8px 0' }}>Тариф</th>
                                  <th style={{ padding: '8px 0' }}>Оплачен до</th>
                                </tr>
                              </thead>
                              <tbody>
                                {c.users.map(u => (
                                  <tr key={u.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                                    <td style={{ padding: '8px 0' }}>{u.name || '—'}</td>
                                    <td style={{ padding: '8px 0' }}>{u.email}</td>
                                    <td style={{ padding: '8px 0' }}>{TARIFFS[(u.tariff as keyof typeof TARIFFS) || 'month'].label}</td>
                                    <td style={{ padding: '8px 0' }}>{u.demoExpiresAt ? new Date(u.demoExpiresAt).toLocaleDateString('ru-RU') : '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      
</div>)}

      {/* Контент вкладки "История платежей" */}
      {activeTab === 'transactions' && (<div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
          <input type="date" value={histDateFrom} onChange={e => setHistDateFrom(e.target.value)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1' }} />
          <input type="date" value={histDateTo} onChange={e => setHistDateTo(e.target.value)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1' }} />
          <button onClick={() => { setHistDateFrom(''); setHistDateTo(''); }} style={{ padding: '8px 16px', background: '#e2e8f0', color: '#475569', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Сброс</button>
          <button onClick={() => {
            const ws = XLSX.utils.json_to_sheet(billingData.transactions.map((t: any) => ({ 'Дата': t.date, 'Сумма': t.amount, 'Компания': t.company })));
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'История');
            saveAs(new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], { type: 'application/octet-stream' }), 'История_платежей.xlsx');
          }} style={{ padding: '8px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Экспорт в Excel</button>
        </div>
        <div style={glassStyle}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#64748b' }}>
                  <th style={{ padding: '12px 8px', fontWeight: 600 }}>ID / Дата</th>
                  <th style={{ padding: '12px 8px', fontWeight: 600 }}>Компания</th>
                  <th style={{ padding: '12px 8px', fontWeight: 600 }}>Пользователь</th>
                  <th style={{ padding: '12px 8px', fontWeight: 600 }}>Тариф</th>
                  <th style={{ padding: '12px 8px', fontWeight: 600 }}>Сумма</th>
                </tr>
              </thead>
              <tbody>
                {billingData.transactions.map(t => (
                  <tr key={t.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '16px 8px' }}>
                      <div style={{ fontWeight: 600 }}>{t.id}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>{t.date}</div>
                    </td>
                    <td style={{ padding: '16px 8px' }}>{t.company}</td>
                    <td style={{ padding: '16px 8px' }}>{t.user}</td>
                    <td style={{ padding: '16px 8px' }}>{t.tariff}</td>
                    <td style={{ padding: '16px 8px', fontWeight: 600 }}>{t.amount.toLocaleString('ru-RU')} ₽</td>
                  </tr>
                ))}
                {billingData.transactions.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
                      Нет данных о платежах
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      
</div>)}
      {selectedCompanyForBilling && <CompanyBillingModal company={selectedCompanyForBilling} users={users} onClose={() => setSelectedCompanyForBilling(null)} />}


    </div>
  );
}
